import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ExpandIcon, XIcon } from 'lucide-react';
import { ProjectLightbox } from './ProjectLightbox';
import { ImageSourceBadge } from '../ui/ImageSourceBadge';
import { ReviewNote } from '../ui/ReviewNote';
import { useProjectDrawer } from '../../contexts/ProjectDrawerContext';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { buttonClass } from '../../utils/buttonStyles';
import { categoryLabel } from '../../utils/projects';
import { EASE_OUT } from '../../utils/motion';
export function ProjectDrawer() {
  const {
    activeProject: project,
    closeProject
  } = useProjectDrawer();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const lastThumbRef = useRef<HTMLButtonElement | null>(null);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const reduceMotion = useReducedMotion();
  useBodyScrollLock(!!project);
  useFocusTrap(panelRef, !!project && lightboxIndex === null);
  useEffect(() => {
    setLightboxIndex(null);
    if (project) {
      scrollRef.current?.scrollTo(0, 0);
      closeRef.current?.focus();
    }
  }, [project?.id]);
  useEffect(() => {
    if (!project) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && lightboxIndex === null) closeProject();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [project, lightboxIndex, closeProject]);
  const openLightbox = (index: number, trigger: HTMLButtonElement) => {
    lastThumbRef.current = trigger;
    setLightboxIndex(index);
  };
  const closeLightbox = () => {
    setLightboxIndex(null);
    window.setTimeout(() => lastThumbRef.current?.focus({
      preventScroll: true
    }), 0);
  };
  const hidden = reduceMotion ? {
    opacity: 0
  } : isDesktop ? {
    x: '100%'
  } : {
    y: '100%'
  };
  const shown = reduceMotion ? {
    opacity: 1
  } : isDesktop ? {
    x: 0
  } : {
    y: 0
  };
  const details: [string, string][] = project ? [project.location ? ['Location', project.location] : null, ...project.parties.map(party => [party.role, party.name] as [string, string]), project.reference ? ['Project reference', project.reference] : null, project.period ? ['Period', project.period] : null, project.status ? ['Status', project.status] : null, project.value ? ['Approximate value', project.value] : null].filter(Boolean) as [string, string][] : [];
  return <AnimatePresence data-id="element-453" data-mp="3a6j2zk11m5m3">
      {project && <>
          <motion.div key="drawer-backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: EASE_OUT
      }} className="fixed inset-0 z-[60] bg-black/70" onClick={closeProject} aria-hidden="true" data-id="element-454" data-mp="3a7aqs8oeez1t" />
          <motion.div key="drawer-panel" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="project-drawer-title" initial={hidden} animate={shown} exit={hidden} transition={{
        duration: 0.3,
        ease: EASE_OUT
      }} className="fixed inset-0 z-[61] flex flex-col bg-charcoal-900 md:inset-y-0 md:left-auto md:right-0 md:w-[min(760px,92vw)] md:border-l-4 md:border-brand-red" data-id="element-455" data-mp="13hh5cypkhk8e">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3 md:px-8" data-id="element-456" data-mp="2146c1lv7sl0l">
              <span className="font-display text-[14px] font-bold uppercase tracking-[0.16em] text-brand-light" data-id="element-457" data-mp="1w5wfp7xr7ede">
                {categoryLabel(project.category)}
              </span>
              <button ref={closeRef} type="button" onClick={closeProject} className="flex h-11 w-11 items-center justify-center text-white transition-colors duration-150 hover:text-brand-light" aria-label="Close project details" data-id="element-458" data-mp="apf3znlpioqo">
                <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-459" data-mp="onwq5gvyrt6h" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto" data-id="element-460" data-mp="tlf3rkkxewkt">
              <button type="button" onClick={event => openLightbox(0, event.currentTarget)} className="group relative block aspect-[16/9] w-full overflow-hidden bg-charcoal-950" aria-label={`Enlarge image: ${project.images[0].caption ?? project.images[0].alt}`} data-id="element-461" data-mp="yf172m579o3y">
                <img src={project.images[0].src} alt={project.images[0].alt} className="h-full w-full object-cover" data-id="element-462" data-mp="1cbek9j2pb3cm" />
                <span className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center bg-charcoal-950/85 text-white transition-colors duration-150 group-hover:bg-brand-action" data-id="element-463" data-mp="1hiuug2n8tjpu">
                  <ExpandIcon className="h-4 w-4" aria-hidden="true" data-id="element-464" data-mp="18hwqtlgpve7s" />
                </span>
                <ImageSourceBadge source={project.images[0].source} className="absolute left-3 top-3" data-id="element-465" data-mp="2585ybszw3u87" />
              </button>

              <div className="px-5 py-8 md:px-8" data-id="element-466" data-mp="208t0yebe74rd">
                {project.location && <p className="text-sm uppercase tracking-[0.12em] text-steel-400" data-id="element-467" data-mp="1vhuhj5v3xqjw">{project.location}</p>}
                <h2 id="project-drawer-title" className="mt-2 font-display text-[34px] font-extrabold uppercase leading-[0.95] text-white md:text-[42px]" data-id="element-468" data-mp="1rfpu4ov95q5p">
                  {project.title}
                </h2>

                <p className="mt-5 text-[17px] leading-relaxed text-steel-100" data-id="element-469" data-mp="haof95rppgox">{project.summary}</p>
                {project.relationship && <p className="mt-4 border-l-[3px] border-brand-red pl-4 text-[15px] leading-relaxed text-steel-200" data-id="element-470" data-mp="m86gsb3egtmw">
                    {project.relationship}
                  </p>}

                {project.reviewNotes && project.reviewNotes.length > 0 && <ReviewNote className="mt-6" data-id="element-471" data-mp="3up1fded5zrv6">
                    <ul className="list-disc space-y-1 pl-4" data-id="element-472" data-mp="3urdvdlzd3s5l">
                      {project.reviewNotes.map(note => <li key={note} data-id="element-473" data-mp="3krdcg15ene1x">{note}</li>)}
                    </ul>
                  </ReviewNote>}

                <h3 className="mt-10 font-display text-[20px] font-bold uppercase tracking-[0.06em] text-white" data-id="element-474" data-mp="3pqq8nci61xh6">
                  Project details
                </h3>
                <dl className="mt-4 divide-y divide-white/10 border-y border-white/10" data-id="element-475" data-mp="1nz3e50z2fkfy">
                  {details.map(([label, value]) => <div key={`${label}-${value}`} className="grid gap-1 py-3 sm:grid-cols-[180px_1fr] sm:gap-6" data-id="element-476" data-mp="38h5hotx1zr2r">
                      <dt className="text-[13px] font-semibold uppercase tracking-[0.1em] text-steel-400" data-id="element-477" data-mp="33fyaavytotwm">{label}</dt>
                      <dd className="text-[15px] text-white" data-id="element-478" data-mp="3dg8pgm4f4bef">{value}</dd>
                    </div>)}
                </dl>
                {project.value && <ReviewNote className="mt-3" data-id="element-479" data-mp="3r9ggabkod2v2">
                    Values are as listed in the October 2026 company profile. Confirm before publishing.
                  </ReviewNote>}

                <h3 className="mt-10 font-display text-[20px] font-bold uppercase tracking-[0.06em] text-white" data-id="element-480" data-mp="4xt1s8r3ymfo">
                  Scope of work
                </h3>
                <ul className="mt-4 space-y-2.5" data-id="element-481" data-mp="26sbjpeofk5r3">
                  {project.scope.map(item => <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-steel-100" data-id="element-482" data-mp="21u08p0mefp7a">
                      <span className="mt-2 h-2 w-2 shrink-0 bg-brand-red" aria-hidden="true" data-id="element-483" data-mp="1fyqi21iham4e" />
                      {item}
                    </li>)}
                </ul>

                {project.components && <>
                    <h3 className="mt-10 font-display text-[20px] font-bold uppercase tracking-[0.06em] text-white" data-id="element-484" data-mp="1uope9yqh3nh8">
                      System components
                    </h3>
                    <dl className="mt-4 grid gap-x-8 sm:grid-cols-2" data-id="element-485" data-mp="11ziiv8mbfe07">
                      {project.components.map(component => <div key={component.label} className="border-t border-white/10 py-3" data-id="element-486" data-mp="16xjzzo06l391">
                          <dt className="font-display text-[16px] font-semibold uppercase tracking-[0.04em] text-white" data-id="element-487" data-mp="1bxy7n4xveb6j">
                            {component.label}
                          </dt>
                          <dd className="mt-1 text-sm leading-relaxed text-steel-300" data-id="element-488" data-mp="2nc218a0jpxb2">{component.description}</dd>
                        </div>)}
                    </dl>
                  </>}

                {project.images.length > 1 && <>
                    <h3 className="mt-10 font-display text-[20px] font-bold uppercase tracking-[0.06em] text-white" data-id="element-489" data-mp="1ywy0gexw293">
                      Gallery
                    </h3>
                    <ul className="mt-4 grid grid-cols-2 gap-3" data-id="element-490" data-mp="3o6n0sio9ctem">
                      {project.images.map((image, index) => <li key={`${image.src}-${index}`} data-id="element-491" data-mp="3k48grv8urnlr" data-mpi={index}>
                          <button type="button" onClick={event => openLightbox(index, event.currentTarget)} className="group block w-full text-left" aria-label={`Open image ${index + 1}: ${image.caption ?? image.alt}`} data-id="element-492" data-mp="3f670fi2ta2dz">
                            <span className="relative block aspect-[4/3] overflow-hidden bg-charcoal-950" data-id="element-493" data-mp="18ga7mn2ow8mx">
                              <img src={image.src} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-494" data-mp="srze84rxgrqa" />
                            </span>
                            {image.caption && <span className="mt-2 block text-[13px] leading-snug text-steel-300" data-id="element-495" data-mp="5480qxuni8nf">{image.caption}</span>}
                          </button>
                        </li>)}
                    </ul>
                  </>}
              </div>
            </div>

            <div className="border-t border-white/10 bg-charcoal-950 px-5 py-4 md:px-8" data-id="element-496" data-mp="3mjxyit91lgpi">
              <Link to={`/contact?project=${project.id}#enquiry`} onClick={closeProject} className={buttonClass('primary', 'md', 'w-full sm:w-auto')} data-id="element-497" data-mp="3ijnvnpgjr6ev">
                Discuss a Similar Project
              </Link>
            </div>
          </motion.div>
          {lightboxIndex !== null && <ProjectLightbox key="drawer-lightbox" images={project.images} index={lightboxIndex} title={project.title} onClose={closeLightbox} onNavigate={setLightboxIndex} data-id="element-498" data-mp="2yex2zcky2jwf" />}
        </>}
    </AnimatePresence>;
}